<script setup lang="ts">
definePageMeta({ layout: 'documentation' })
useSeoMeta({
  title: 'Movement & Vision Analysis - Sailor Documentation'
})

const metrics = [
  {
    label: 'Body movement through a tack',
    caption: 'How fast, and how far, your body crosses the boat as you tack',
    icon: 'mdi-rotate-3d-variant',
  },
  {
    label: 'Shoulder-crossing trace',
    caption: 'Your position across the boat, second by second, through the turn',
    icon: 'mdi-arrow-left-right',
  },
  {
    label: 'Body activity downwind',
    caption: 'How much you are moving, pumping and working the boat on a run',
    icon: 'mdi-run-fast',
  },
  {
    label: 'Vision downwind',
    caption: 'How much you are looking around — turning your head to read pressure on the water',
    icon: 'mdi-eye-outline',
  },
]
</script>

<template>
  <div>
    <h1>Movement & Vision Analysis</h1>
    <p>
      Movement & Vision Analysis reads your sailing video and turns what your body is doing into numbers. It
      looks at two things: how you move through a tack, and how active you are — and where you are looking —
      while sailing downwind. It runs automatically; there is no separate step to trigger and nothing to
      configure.
    </p>

    <v-alert type="info" variant="tonal" density="comfortable" class="mb-6">
      Movement & Vision Analysis is currently limited to <strong>single-handed dinghy sailing</strong> — one sailor on
      board. See <a href="#single-handed">Why single-handed only</a> below. Tack analysis also requires a Pro
      account; if you don't see it on your sessions, email
      <a href="mailto:info@sailingmetrics.com?subject=Movement%20%26%20Vision%20Analysis%20access">info@sailingmetrics.com</a>.
    </v-alert>

    <h2>What it measures</h2>
    <v-container fluid class="pa-0">
      <v-row>
        <v-col v-for="m in metrics" :key="m.label" cols="12" sm="6">
          <v-card class="fill-height">
            <v-card-title class="d-flex align-center ga-2 text-body-1">
              <v-icon :icon="m.icon" color="primary" size="20" />
              {{ m.label }}
            </v-card-title>
            <v-card-text>{{ m.caption }}</v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <h2>Movement through a tack</h2>
    <v-container fluid class="pa-0">
      <v-row>
        <v-col cols="12" md="8">
          <p>
            This runs after you upload a session that has video attached. For every tack the analysis already
            detected from your GPS track, Movement & Vision Analysis pulls a roughly ten-second clip centred on
            the tack and estimates your body pose in each frame — a full skeleton of shoulders, hips, arms and
            legs.
          </p>
          <p>From that it produces, for each tack:</p>
          <ul>
            <li>
              <strong>An annotated clip.</strong> The ten-second tack clip with the detected skeleton drawn on
              top, so you can see frame by frame how you came across the boat.
            </li>
            <li>
              <strong>Body-movement speed.</strong> The peak sideways speed of your body's centre of mass as
              you cross the boat, measured as a fraction of the frame width per second so it doesn't matter how
              far the camera was zoomed in. A higher number means a faster, more athletic crossing; a lower
              number a slower, more gradual one.
            </li>
            <li>
              <strong>Shoulder-crossing trace.</strong> Your shoulder position mapped from one side of the boat
              (&minus;1) to the other (+1), plotted against time through the turn. Port-to-starboard and
              starboard-to-port tacks are flipped to read the same way and lined up at the moment of the tack,
              so you can lay every tack in the session over each other and see which ones you crossed early,
              late or in a rush.
            </li>
          </ul>
          <p>
            You'll find all of this in the analysis view under <strong>Maneuvers</strong>: the clip and
            per-tack numbers sit in the tack list, and there are two comparison charts — body-movement speed
            against the speed you lost in the tack, and the shoulder trace of every tack on one set of axes.
          </p>
        </v-col>
        <v-col cols="12" md="4">
          <video controls style="max-width: 100%; border-radius: 4px;">
            <source src="/videos/tack-analysis-reel.mp4" type="video/mp4">
            Your browser does not support the video tag.
          </video>
        </v-col>
      </v-row>
    </v-container>

    <h2>Activity and vision downwind</h2>
    <p>
      This one runs live, on your phone, during a Training Mode session — it uses the front camera pointed at
      you while you sail. Roughly once a second the app estimates your body pose and the direction your head is
      facing, on device, alongside the usual GPS and sensor recording. It only does this on downwind legs.
    </p>
    <p>Two figures come out of it, both measured as rates of change between frames:</p>
    <ul>
      <li>
        <strong>Body activity.</strong> How much your body is moving between one moment and the next — a proxy
        for hiking, pumping, moving your weight around and generally working the boat down the run.
      </li>
      <li>
        <strong>Vision.</strong> How much you are turning your head — a proxy for looking around to read the
        pressure and the puffs on the water rather than staring straight ahead. If your face isn't visible to
        the camera for a stretch, this is simply left blank for that stretch rather than counted as zero.
      </li>
    </ul>
    <p>
      Training Mode uses both in its spoken commentary. At the end of each downwind leg — and in shorter
      updates roughly every 30 seconds during a long one — it compares the leg you just sailed against your own
      downwind average so far this session, for example telling you that you were looking around more than
      usual, or working the boat less than usual, on that run. It's descriptive, not instructional: neither
      more nor less movement or scanning is treated as "better", it just reports what changed. The figures are
      saved with the session alongside the commentary transcript.
    </p>

    <h2 id="single-handed">Why single-handed only</h2>
    <p>
      Both pipelines analyse exactly one person. The tack analysis picks the largest person it detects in the
      frame; the downwind analysis tracks a single pose from the front camera. With two or more people on
      board, the metrics either jump between crew or lock onto whoever is most visible, and the numbers stop
      meaning anything. Until crew-aware tracking is added, Movement & Vision Analysis is only offered for
      single-handed dinghies, where there is one sailor to follow.
    </p>
    <p>
      If you sail a keelboat or with crew and would be interested in helping us develop multi-person tracking,
      we'd like to hear from you — get in touch through our <nuxt-link to="/contact">contact form</nuxt-link>
      with what you sail and what you'd want to see.
    </p>
    <p>
      It also depends on you actually being in shot. For tack analysis that means the session video needs to
      show you through the tack; for downwind activity and vision it means the phone's front camera needs a
      reasonably clear view of you while recording a Training Mode session.
    </p>

    <h2>What you need</h2>
    <ul>
      <li>
        <strong>Movement through a tack</strong> — a Pro account, and an uploaded session with a video that
        shows you on the boat.
      </li>
      <li>
        <strong>Activity and vision downwind</strong> — a Training Mode session recorded with the app, with
        the front camera able to see you. Processing happens on the phone; nothing extra is uploaded for it.
      </li>
    </ul>
  </div>
</template>
